// ============================================================================
// GST MAPPED PREVIEW — G-1 slice 3b (ADR-0025 §5)
// ============================================================================
// What a mapping profile makes of a submission's staged rows: typed records,
// rate-wise totals over ALL of them, and every error named against the row it
// came from. The totals are the thing to check against the client's own
// summary before recording the profile.
//
// Nothing here writes except "Use this profile", which records WHICH profile
// maps the submission. The typed records are re-derived from staging whenever
// they are needed, so a corrected profile re-maps the same rows.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { gstSubmissionService } from 'api/services/gstSubmissionService';
import type {
  GstDataSubmission,
  GstEngagement,
  GstMappedRecord,
  GstMappingPreview,
  GstMappingProfile,
  GstProfileCandidate,
} from 'api/types/gstEngagement.types';
import { FIELD_LABELS } from './gstProfileForm';
import GstProfileMapperModal from './GstProfileMapperModal';

interface Props {
  engagement: GstEngagement;
  submission: GstDataSubmission;
  /** Recording a profile and building one in the mapper are writes; they follow the `edit` grant. */
  canSelect: boolean;
  /** Accepting or rejecting a client's proposed profile follows the `approve` grant. */
  canApprove?: boolean;
  onSelected?: (s: GstDataSubmission) => void;
}

type MapperState = { open: false } | { open: true; base: GstMappingProfile | null; seed: string | null };

const formatDate = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const AMOUNT_FIELDS = new Set(['document_value', 'taxable_value', 'igst', 'cgst', 'sgst', 'cess', 'total_value', 'round_off']);

const money = (n: number) => n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const show = (field: string, v: string | number | boolean | null): string => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'boolean') return v ? 'Y' : 'N';
  if (typeof v === 'number' && AMOUNT_FIELDS.has(field)) return money(v);
  return String(v);
};

const GstMappedPreview: React.FC<Props> = ({ engagement, submission, canSelect, canApprove = false, onSelected }) => {
  const [candidates, setCandidates] = useState<GstProfileCandidate[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [profileId, setProfileId] = useState('');
  const [preview, setPreview] = useState<GstMappingPreview | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mapper, setMapper] = useState<MapperState>({ open: false });
  const [templateTypes, setTemplateTypes] = useState<string[]>([]);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [reviewing, setReviewing] = useState(false);

  /** Loads the candidates; `prefer` picks a profile by id (one just saved), else the recorded one, else the first that fits. */
  const reloadCandidates = useCallback(async (prefer?: string) => {
    try {
      const c = await gstSubmissionService.getMappingCandidates(engagement.id, submission.id);
      setCandidates(c);
      const pick = (prefer ? c.find((x) => x.profile.id === prefer) : undefined)
        ?? c.find((x) => x.selected) ?? c.find((x) => x.applicable);
      setProfileId(pick?.profile.id ?? '');
      setPage(1);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Could not load the mapping profiles.');
    } finally {
      setLoaded(true);
    }
  }, [engagement.id, submission.id]);

  useEffect(() => { void reloadCandidates(); }, [reloadCandidates]);

  useEffect(() => {
    gstSubmissionService.getTemplateTypes().then(setTemplateTypes).catch(() => setTemplateTypes([]));
  }, []);

  const hasTemplate = templateTypes.includes(submission.documentType);
  const downloadTemplate = () => {
    gstSubmissionService.downloadTemplate(submission.documentType)
      .catch((err: any) => notificationService.toast.error(err?.response?.data?.message ?? 'Could not download the template.'));
  };

  const accept = async () => {
    if (!current) return;
    setAcceptOpen(false);
    setReviewing(true);
    try {
      const p = await gstSubmissionService.acceptMappingProfile(engagement.id, current.profile.id);
      notificationService.toast.success(`'${p.name}' v${p.version} accepted.`);
      await reloadCandidates(p.id);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Could not accept the profile.');
    } finally {
      setReviewing(false);
    }
  };

  const reject = async () => {
    if (!current || rejectReason.trim().length < 3) return;
    setReviewing(true);
    try {
      const p = await gstSubmissionService.rejectMappingProfile(engagement.id, current.profile.id, rejectReason.trim());
      notificationService.toast.success(`'${p.name}' v${p.version} rejected.`);
      setRejecting(false);
      setRejectReason('');
      await reloadCandidates();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Could not reject the profile.');
    } finally {
      setReviewing(false);
    }
  };

  const load = useCallback(async () => {
    if (!profileId) { setPreview(null); return; }
    setLoading(true);
    try {
      setPreview(await gstSubmissionService.previewMapping(engagement.id, submission.id, { profileId, page, pageSize, errorsOnly }));
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Could not map the rows.');
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, [engagement.id, submission.id, profileId, page, pageSize, errorsOnly]);

  useEffect(() => { void load(); }, [load]);

  const current = candidates.find((c) => c.profile.id === profileId);

  const options = useMemo(() => candidates.map((c) => ({
    value: c.profile.id,
    label: `${c.profile.name} · ${c.profile.profileKind === 'SYSTEM' ? 'system' : 'this client'} v${c.profile.version}`
      + (c.profile.profileStatus === 'PROPOSED' ? ' · awaiting review' : '')
      + (c.selected ? ' · in use' : c.applicable ? ' · fits' : ` · missing ${c.missingHeaders.length} column(s)`),
  })), [candidates]);

  const select = useCallback(async () => {
    if (!profileId) return;
    setSaving(true);
    try {
      const s = await gstSubmissionService.selectMappingProfile(engagement.id, submission.id, profileId);
      notificationService.toast.success(`${current?.profile.name ?? 'Profile'} recorded for this submission.`);
      setCandidates((cs) => cs.map((c) => ({ ...c, selected: c.profile.id === profileId })));
      onSelected?.(s);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Could not record the profile.');
    } finally {
      setSaving(false);
    }
  }, [engagement.id, submission.id, profileId, current, onSelected]);

  const multiSheet = (submission.parseStats?.sheets.length ?? 0) > 1;

  const columns: SyutGridColumn<GstMappedRecord>[] = useMemo(() => {
    const cols: SyutGridColumn<GstMappedRecord>[] = [
      { id: 'row', header: '#', size: 60, enableSorting: false, cell: ({ row }: any) => {
        const r = row.original as GstMappedRecord;
        return <span style={{ color: 'var(--text-secondary)' }} title={r.parentRowIndex ? `line of row ${r.parentRowIndex}` : undefined}>{r.rowIndex}{r.parentRowIndex ? ' ↳' : ''}</span>;
      } },
    ];
    if (multiSheet) {
      cols.push({ id: 'sheet', header: 'Sheet', size: 120, enableSorting: false, cell: ({ row }: any) => (row.original as GstMappedRecord).sheet });
      cols.push({ id: 'kind', header: 'Kind', size: 90, enableSorting: false, cell: ({ row }: any) => {
        const k = (row.original as GstMappedRecord).recordKind;
        return k === 'DOCUMENT' ? 'Invoice' : k === 'SUMMARY' ? 'Summary' : 'Line';
      } });
    }
    for (const f of preview?.columns ?? []) {
      cols.push({
        id: f, header: FIELD_LABELS[f] ?? f, size: AMOUNT_FIELDS.has(f) ? 110 : f === 'counterparty_name' || f === 'description' ? 170 : 110,
        enableSorting: false,
        cell: ({ row }: any) => {
          const v = (row.original as GstMappedRecord).fields[f] ?? null;
          return (
            <span style={{ display: 'block', textAlign: AMOUNT_FIELDS.has(f) || f === 'rate' || f === 'quantity' ? 'right' : 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {show(f, v)}
            </span>
          );
        },
      });
    }
    cols.push({
      id: 'issues', header: 'Issues', size: 260, enableSorting: false,
      cell: ({ row }: any) => {
        const r = row.original as GstMappedRecord;
        if (r.errors.length === 0 && r.warnings.length === 0) return '';
        return (
          <span title={[...r.errors, ...r.warnings].join('\n')} style={{ fontSize: '0.75rem', color: r.errors.length ? '#dc2626' : '#d97706', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
            {(r.errors[0] ?? r.warnings[0])}{r.errors.length + r.warnings.length > 1 ? ` (+${r.errors.length + r.warnings.length - 1})` : ''}
          </span>
        );
      },
    });
    return cols;
  }, [preview, multiSheet]);

  const mapperModal = mapper.open && (
    <GstProfileMapperModal
      isOpen
      engagement={engagement}
      submission={submission}
      base={mapper.base}
      seedDefinition={mapper.seed}
      onClose={() => setMapper({ open: false })}
      onSaved={(p) => { setMapper({ open: false }); void reloadCandidates(p.id); }}
    />
  );

  const templateLink = hasTemplate && (
    <button type="button" onClick={downloadTemplate}
      style={{ fontSize: '0.75rem', padding: 0, border: 'none', background: 'transparent', color: '#2563eb', textDecoration: 'underline', cursor: 'pointer' }}
      title="An Excel template whose columns this module reads with no mapping">
      Download the standard template
    </button>
  );

  if (!loaded) return null;

  if (candidates.length === 0) {
    return (
      <>
        <div style={{ border: '1px dashed var(--border-color)', borderRadius: 6, padding: '1rem', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
          <div style={{ marginBottom: 8 }}>No mapping profile exists yet for this stream.</div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            {canSelect && (
              <button className="btn btn-primary" onClick={() => setMapper({ open: true, base: null, seed: null })}>
                Map this client's columns
              </button>
            )}
            {templateLink}
          </div>
        </div>
        {mapperModal}
      </>
    );
  }

  const isProposed = current?.profile.profileStatus === 'PROPOSED';
  const isLearned = current?.profile.profileKind === 'LEARNED';

  return (
    <>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', fontSize: '0.75rem' }}>
        {canSelect && (
          <>
            <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '3px 10px' }}
              onClick={() => setMapper({ open: true, base: null, seed: null })}>
              New profile for this client
            </button>
            {current && isLearned && !isProposed && (
              <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '3px 10px' }}
                onClick={() => setMapper({ open: true, base: current.profile, seed: null })}>
                Edit as new version
              </button>
            )}
            {current && !isLearned && (
              <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '3px 10px' }}
                title="Start a profile for this client from this system profile"
                onClick={() => setMapper({ open: true, base: null, seed: current.profile.definition })}>
                Copy as client profile
              </button>
            )}
          </>
        )}
        <span style={{ flex: 1 }} />
        {templateLink}
      </div>

      {current && isProposed && (
        <div style={{ border: '1px solid #f59e0b', background: 'var(--bg-secondary)', borderRadius: 6, padding: '0.5rem 0.625rem', marginBottom: 10, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="badge badge-pending">Awaiting review</span>
            <span style={{ flex: 1 }}>
              v{current.profile.version}{current.profile.supersedesProfileId ? ' — a new version of a profile in use' : ''}, proposed
              {current.profile.createdByName ? ` by ${current.profile.createdByName}` : ''}{current.profile.createdAt ? ` on ${formatDate(current.profile.createdAt)}` : ''}.
              {' '}It can map files now; a return cannot be finalised from it until it is accepted.
              {current.profile.notes ? <> Note: <em>{current.profile.notes}</em></> : null}
            </span>
            {canApprove && !rejecting && (
              <>
                <button className="btn btn-primary" style={{ fontSize: '0.75rem', padding: '3px 10px' }} disabled={reviewing} onClick={() => setAcceptOpen(true)}>Accept</button>
                <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '3px 10px' }} disabled={reviewing} onClick={() => setRejecting(true)}>Reject</button>
              </>
            )}
          </div>
          {canApprove && rejecting && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6 }}>
              <input className="form-input" style={{ flex: 1, fontSize: '0.8125rem', padding: '3px 8px' }} maxLength={500} autoFocus
                placeholder="Why — the preparer sees this (e.g. CGST is the second CGST column)" value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)} />
              <button className="btn btn-danger" style={{ fontSize: '0.75rem', padding: '3px 10px' }}
                disabled={reviewing || rejectReason.trim().length < 3} onClick={() => { void reject(); }}>Reject profile</button>
              <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '3px 10px' }} disabled={reviewing}
                onClick={() => { setRejecting(false); setRejectReason(''); }}>Cancel</button>
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 360px' }}>
          <SyutSelect
            label="Mapping profile"
            options={options}
            value={profileId}
            isMulti={false}
            isClearable={false}
            size="sm"
            onChange={(v) => { setProfileId((v as string) || ''); setPage(1); }}
          />
        </div>
        <label style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 6 }}>
          <input type="checkbox" checked={errorsOnly} onChange={(e) => { setErrorsOnly(e.target.checked); setPage(1); }} />
          Only rows with errors
        </label>
        {canSelect && (
          <button
            className="btn btn-primary"
            onClick={() => { void select(); }}
            disabled={saving || !current || !current.applicable || current.selected}
            title={current && !current.applicable ? 'This profile does not fit the file' : undefined}
          >
            {current?.selected ? 'In use' : saving ? 'Recording…' : 'Use this profile'}
          </button>
        )}
      </div>

      {current && !current.applicable && current.missingHeaders.length > 0 && (
        <div style={{ border: '1px solid #f59e0b', borderRadius: 6, padding: '0.5rem 0.625rem', marginBottom: 10, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
          <strong style={{ color: 'var(--text-primary)' }}>This profile does not fit the file.</strong> Missing:{' '}
          {current.missingHeaders.slice(0, 8).join(' · ')}{current.missingHeaders.length > 8 ? ' …' : ''}
        </div>
      )}

      {preview && (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 10, fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
            <span><strong style={{ color: 'var(--text-primary)' }}>{preview.recordCount.toLocaleString()}</strong> records from {preview.inputRows.toLocaleString()} rows</span>
            <span style={{ color: preview.recordsWithErrors ? '#dc2626' : undefined }}>
              <strong>{preview.recordsWithErrors.toLocaleString()}</strong> with errors
            </span>
            <span style={{ color: preview.recordsWithWarnings ? '#d97706' : undefined }}>
              <strong>{preview.recordsWithWarnings.toLocaleString()}</strong> with warnings
            </span>
          </div>

          {preview.messages.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              {preview.messages.map((m, i) => (
                <div key={i} style={{ fontSize: '0.75rem', color: m.severity === 'ERROR' ? '#dc2626' : '#d97706' }}>{m.message}</div>
              ))}
            </div>
          )}

          {preview.byRate.length > 0 && (
            <table style={{ fontSize: '0.75rem', borderCollapse: 'collapse', marginBottom: 10, minWidth: 520 }}>
              <thead>
                <tr style={{ color: 'var(--text-secondary)', textAlign: 'right' }}>
                  <th style={{ textAlign: 'left', padding: '2px 8px' }}>Rate</th>
                  <th style={{ padding: '2px 8px' }}>Records</th>
                  <th style={{ padding: '2px 8px' }}>Taxable</th>
                  <th style={{ padding: '2px 8px' }}>IGST</th>
                  <th style={{ padding: '2px 8px' }}>CGST</th>
                  <th style={{ padding: '2px 8px' }}>SGST</th>
                  <th style={{ padding: '2px 8px' }}>Cess</th>
                </tr>
              </thead>
              <tbody>
                {preview.byRate.map((t) => (
                  <tr key={String(t.rate)} style={{ textAlign: 'right', borderTop: '1px solid var(--border-color)' }}>
                    <td style={{ textAlign: 'left', padding: '2px 8px' }}>{t.rate === null || t.rate === undefined ? 'no rate' : `${t.rate}%`}</td>
                    <td style={{ padding: '2px 8px' }}>{t.count.toLocaleString()}</td>
                    <td style={{ padding: '2px 8px' }}>{money(t.taxableValue)}</td>
                    <td style={{ padding: '2px 8px' }}>{money(t.igst)}</td>
                    <td style={{ padding: '2px 8px' }}>{money(t.cgst)}</td>
                    <td style={{ padding: '2px 8px' }}>{money(t.sgst)}</td>
                    <td style={{ padding: '2px 8px' }}>{money(t.cess)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <SyutGrid
              data={preview.items as any}
              columns={columns as any}
              enablePagination
              manualPagination
              totalCount={preview.totalCount}
              currentPage={page - 1}
              pageSize={pageSize}
              onPageChange={(idx: number) => setPage(idx + 1)}
              onPageSizeChange={(size: number) => { setPageSize(size); setPage(1); }}
              emptyMessage={loading ? 'Mapping…' : errorsOnly ? 'No rows with errors.' : 'No records.'}
            />
          </div>
        </>
      )}

      {mapperModal}

      <ConfirmDialog
        open={acceptOpen}
        title="Accept this profile?"
        message={current
          ? `'${current.profile.name}' v${current.profile.version} becomes the profile in use for this client${current.profile.supersedesProfileId ? ', and the version it replaces is retired' : ''}. Check the rate-wise totals against the client's own summary first.`
          : ''}
        confirmLabel="Accept"
        cancelLabel="Cancel"
        variant="success"
        loading={reviewing}
        loadingMessage="Accepting…"
        onConfirm={() => { void accept(); }}
        onCancel={() => setAcceptOpen(false)}
      />
    </>
  );
};

export default GstMappedPreview;
